import { LIMITS, UPLOAD_TOO_LARGE_MESSAGE, limitInMb } from '@helpdesk/shared';

/**
 * Zugang zur API: kleine fetch-Helfer mit einheitlicher Fehlerbehandlung.
 * Die Meldungen des Servers sind für Anwender geschrieben und werden direkt
 * angezeigt. Typen der Antworten kommen aus dem Server (`types.ts`).
 */
export type ApiError = { error: { code: string; message: string } };

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

/** Keine Antwort vom Server. Der Browser sagt dazu nur „Failed to fetch“, darum steht der Text für Anwender hier. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Keine Verbindung zum Server. Bitte WLAN oder Mobilfunk prüfen und es dann erneut versuchen.', { cause });
    this.name = 'NetworkError';
  }
}

/** Antworten ohne unsere Fehlerform: Proxy beim Deployment, Middleware mit reinem Text. */
function messageForStatus(status: number): string {
  if (status >= 500) return 'Der Server ist gerade nicht erreichbar. Bitte in einer Minute erneut versuchen.';
  if (status === 403) return 'Die Anfrage wurde abgelehnt. Bitte die Seite neu laden.';
  return `Die Anfrage ist fehlgeschlagen (Status ${status}). Bitte die Seite neu laden und erneut versuchen.`;
}

export async function unwrap<T>(response: Response): Promise<T> {
  if (response.ok) {
    try {
      return (await response.json()) as T;
    } catch {
      // Auch eine WLAN-Anmeldeseite oder das SPA-Fallback antworten mit 200. Ob ein POST durchkam, ist offen.
      throw new ApiRequestError(
        'Die Antwort des Servers war nicht lesbar. Der Vorgang kann trotzdem angekommen sein. Bitte die Seite neu laden und prüfen.',
        response.status,
        'unreadable_response',
      );
    }
  }
  let message = messageForStatus(response.status);
  let code: string | undefined;
  try {
    const body = (await response.json()) as ApiError;
    // Ohne `code` stammt der Körper nicht aus unserer Fehlerform, sein Text gehört nicht zum Anwender.
    if (body?.error?.code && body.error.message) message = body.error.message;
    code = body?.error?.code;
  } catch {
    /* kein JSON */
  }
  throw new ApiRequestError(message, response.status, code);
}

/**
 * Trennt den Netzausfall vom Programmierfehler: `fetch` lehnt ab, wenn keine Antwort kam, sonst nur
 * bei ungültigen Argumenten, und die prüft hier schon der `Request`-Konstruktor.
 */
export async function request(url: string, init: RequestInit): Promise<Response> {
  const req = new Request(url, init);
  try {
    return await fetch(req);
  } catch (cause) {
    throw new NetworkError(cause);
  }
}

export async function getJson<T>(path: string, params?: Record<string, string | number | boolean | undefined | null>): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }
  return unwrap<T>(await request(url.toString(), { credentials: 'same-origin' }));
}

async function send<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  return unwrap<T>(
    await request(path, {
      method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...headers },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  );
}

export const postJson = <T>(path: string, body?: unknown) => send<T>('POST', path, body ?? {});
export const patchJson = <T>(path: string, body: unknown) => send<T>('PATCH', path, body);
export const putJson = <T>(path: string, body: unknown) => send<T>('PUT', path, body);
export const deleteJson = <T>(path: string, body?: unknown) => send<T>('DELETE', path, body);

/** Wie die 413 des Servers, damit Ausgang und Meldung beide Fälle gleich behandeln. */
function tooLarge(message: string): ApiRequestError {
  return new ApiRequestError(message, 413, 'payload_too_large');
}

/** Für alles, was unverkleinert hochgeht. Die Grenze des Servers bleibt als Sicherung. */
export function assertDocumentSize(file: Blob): void {
  if (file.size > LIMITS.documentMaxBytes) {
    throw tooLarge(`Die Datei ist zu groß. Dokumente dürfen höchstens ${limitInMb(LIMITS.documentMaxBytes)} groß sein. Bitte verkleinern oder teilen.`);
  }
}

/** Datei-Upload (ein Foto je Request, §4.6). */
export async function uploadFile<T>(path: string, file: Blob, filename: string, extra: Record<string, string> = {}, headers: Record<string, string> = {}): Promise<T> {
  // Über der Körpergrenze antwortet der Server nach der Kopfzeile, der Browser verliert beim Senden die Verbindung und sieht nie die 413.
  if (file.size > LIMITS.uploadBodyMaxBytes) throw tooLarge(UPLOAD_TOO_LARGE_MESSAGE);
  const form = new FormData();
  form.append('file', file, filename);
  for (const [k, v] of Object.entries(extra)) form.append(k, v);
  return unwrap<T>(await request(path, { method: 'POST', credentials: 'same-origin', body: form, headers }));
}

export function isOfflineError(err: unknown): boolean {
  return err instanceof NetworkError;
}
